
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  Ref,
  ref,
  watch,
  onBeforeUnmount,
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import Dialog from 'primevue/dialog';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import {
  DropdownOption,
  LinkTypeEnum,
  Translation,
  ActionTypeEnum,
  NextStepEnum,
  SendFormActionTypeEnum,
  HistoryType,
  SelectedSyncElement,
} from '@/types';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import FunnelStepSelector from '@/components/template-builder/FunnelStepSelector.vue';
import { Templates } from '@/types/generated-types/graphql';
import { Get } from '@/composables/GraphQL';
import {
  getTemplateIframeDocument,
  getTemplateParentTypeByType,
  highlightWidgetOnTranslationClicked,
  isDisplayTemplate,
  isRichTextingTemplate,
  isWhatsappTemplate,
} from '@/components/template-builder/utils/helpers';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import {
  activate as activateTemplate,
  applyEmbedSettingsToTemplate,
  prepareTemplate,
  setRefreshTemplatesList,
  TemplateEditorState,
  updateTemplateConfiguration,
  addTemplateToFunnels,
  discoverAndAttachSubFunnels,
  createNextStepTemplate,
  addStepToFunnel,
  removeStepFromCurrentFunnelIfUnreferenced,
  FunnelStep,
  generateNextStepName,
  hideLeftToolbar,
  activate,
  beforeSaveTemplate,
  saveTemplate,
  updateSectionsInState,
  updateFormFieldConfig,
  getFormFieldConfig,
  getFormByIdentifier,
  addNewForm,
  insertDisclosureWidgetForEmail,
  removeFormConfig,
  TemplateEditorState as state,
  checkWhatsappHashChangeAndConfirm,
  syncGameFunnelStepsForTemplate,
  resyncGameFunnelSteps,
  hasGameWidgetInOtherFunnelTemplates,
} from '@/composables/template-editor/TemplateEditor';
import { evaluateFormActions, getDefaultEnabledActions } from '@/composables/template-editor/FormActionsEvaluator';
import { FormActionStatus } from '@/composables/template-editor/FormActionsConfig';
import FormActionCard from '@/components/template-builder/FormActionCard.vue';
import { store } from '@/store';
import ProgressSpinner from 'primevue/progressspinner';
import CryptoJS from 'crypto-js';
import { loadImageUntilSuccess } from '@/composables/loadImage';
import {
  TRANSLATION_ATTRIBUTE,
} from '@/components/template-builder/utils/constants';
import { Variable } from '@/components/template-builder/utils/variables-list';
import { UserState } from '@/composables/User';
import { showToastError } from '@/helpers';
import { createHistory } from '@/composables/template-editor/History';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import { CountryAndStateValues } from '@/types/country-state-types';
import { parsePhoneNumber } from 'libphonenumber-js';
import { countriesPhonesCodes } from '@/configs/countries';
import { TemplateTypeEnum } from '@/composables/shop/Templates';

export default defineComponent({
  name: 'BaseLink',

  components: {
    ProgressSpinner,
    TemplateSelectorV2,
    BaseInputText,
    BaseDropdown,
    Dropdown,
    Button,
    Dialog,
    FunnelStepSelector,
    FormActionCard,
    CountryDropdown,
    InputSwitch,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    variablesList: {
      type: Array as PropType<Variable[]>,
      required: false,
      default: () => [],
    },

    linkTypeOptions: {
      type: Array as PropType<LinkTypeEnum[]>,
      required: true,
    },

    disabledLinkTypes: {
      type: Array as PropType<LinkTypeEnum[]>,
      required: false,
      default: () => [],
    },

    initialLinkType: {
      type: String,
      required: false,
      default: null,
    },

    originCall: {
      type: String,
      required: false,
      default: null,
    },

    maxPhoneNumberLength: {
      type: Number,
      required: false,
      default: null,
    },

    idCampaign: {
      type: Number,
      required: false,
      default: null,
    },

    translation: {
      type: Object as PropType<Translation>,
      required: false,
      default: null,
    },

    hideEmailSubject: {
      type: Boolean,
      required: false,
      default: false,
    },

    hideEmailBody: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: {
    'update:modelValue': String,
    'update:linkType': String,
    'update:removeDataSendForm': Boolean,
    click: String,
    'activate-group': String,
    'deactivate-group': String,
  },

  setup(props, context) {
    const { t } = useI18n();
    const baseUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);
    const imageUrl = ref('');
    const mailToPattern = /^mailto:(.*)\?subject=(.*)&[amp;]*body=(.*)$/i;
    const displayTemplateModal = ref(false);
    const selectedTemplate: Ref<Templates | undefined> = ref();
    const displayTemplatePreview = ref(false);
    const hrefValue: Ref<string> = ref(props.modelValue);
    const originCall: Ref<string | null> = ref(props.originCall);
    const addressValue: Ref<string> = ref('');
    const subjectValue: Ref<string> = ref('');
    const bodyValue: Ref<string> = ref('');
    const callNumberValue: Ref<string> = ref('');
    const phoneCountryAndState = ref<CountryAndStateValues>({
      country: (UserState.user.country ?? 'FR').toUpperCase(),
      state: '',
    });
    const updateDataSendForm = ref(false);
    const isGamePrizesRedirect = ref(false);
    const isGameResultRedirect = ref(false);
    const spinBeforeRedirect = ref(false);
    const formActionStatuses: Ref<FormActionStatus[]> = ref([]);

    const sortedAllActions = computed(() => [...formActionStatuses.value].sort((a, b) => {
      // Enabled first, then available, then unavailable
      if (a.isEnabled !== b.isEnabled) return a.isEnabled ? -1 : 1;
      if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
      return a.action.priority - b.action.priority;
    }));

    const currentTemplateHasGame = computed(() => !!state.template?.configuration?.hasGame);

    // Flag to prevent the hrefValue watcher from emitting removeDataSendForm when only toggling spin prefix
    let isSpinPrefixToggle = false;

    watch(spinBeforeRedirect, (newVal) => {
      if (selectedTemplate.value) {
        isSpinPrefixToggle = true;
        const redirectType = newVal ? '#spin_redirect_' : '#redirect_';
        hrefValue.value = `${redirectType}${btoa(String(selectedTemplate.value.id_template))}`;
      }
    });

    const hasFormWidgetInTemplate = (): boolean => {
      const template = getTemplateIframeDocument();
      if (!template) return false;
      return !!template.querySelector('.spm_widget_form');
    };

    const options = computed(() => props.linkTypeOptions
      .filter((key: string) => {
        if (key === LinkTypeEnum.SEND_FORM && !hasFormWidgetInTemplate()) {
          return false;
        }
        return true;
      })
      .map((key: string) => ({
        value: key,
        label: t(`templateBuilder.fields.linkOptions.${key}`),
        disabled: props.disabledLinkTypes.includes(key as LinkTypeEnum),
      })));
    const actionAfterSendFormValue: Ref<SendFormActionTypeEnum> = ref(SendFormActionTypeEnum.CLOSE);
    const sendFormOptions = computed(() => {
      if (props.translation) {
        return [
          {
            value: SendFormActionTypeEnum.CLOSE,
            label: t('templateBuilder.fields.sendFormOptions.close'),
          },
        ];
      }
      return [
        {
          value: SendFormActionTypeEnum.TO_NEXT_STEP,
          label: t('templateBuilder.fields.sendFormOptions.toNextStep'),
        },
        {
          value: SendFormActionTypeEnum.TO_URL,
          label: t('templateBuilder.fields.sendFormOptions.toUrl'),
        },
        {
          value: SendFormActionTypeEnum.CLOSE,
          label: t('templateBuilder.fields.sendFormOptions.close'),
        },
      ];
    });

    const afterSendMessageOptions = computed(() => [
      {
        value: SendFormActionTypeEnum.TO_NEXT_STEP,
        label: t('templateBuilder.fields.sendMessageOptions.toNextStep'),
      },
      {
        value: SendFormActionTypeEnum.CLOSE,
        label: t('templateBuilder.fields.sendMessageOptions.nothing'),
      },
    ]);

    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);
    const actionAfterSendFormHrefValue = ref('');

    const getAnchors = (): DropdownOption[] => {
      const anchors: DropdownOption[] = [];
      const template = getTemplateIframeDocument();

      if (template) {
        if (template.anchors && template.anchors.length) {
          Array.from(template.anchors).forEach((anchor) => {
            const anchorName = anchor.getAttribute('name');

            if (anchorName) {
              const dropdownOption: DropdownOption = { label: anchorName, value: `#${anchorName}` };
              anchors.push(dropdownOption);
            }
          });
        }
      }

      return anchors;
    };

    const baseLinkData: any = computed(() => {
      const namePrimaryTemplate = TemplateEditorState?.template?.informations.name;
      const translationsPrimaryTemplate = TemplateEditorState?.template?.translations ?? [];
      const uniqueRedirects = new Set();

      translationsPrimaryTemplate.forEach((translation) => {
        const { value } = translation;

        const match = value.match(/#(?:spin_)?redirect_(.+)/);
        if (match) {
          try {
            const idRedirect = window.atob(match[1]);
            if (!idRedirect.match(new RegExp('[a-zA-Z]'))) {
              uniqueRedirects.add(idRedirect);
            }
          } catch (e) { /* invalid base64 */ }
        }
      });

      const countRedirectTemplate = uniqueRedirects.size;

      return {
        namePrimaryTemplate,
        countRedirectTemplate,
        originTemplateId: TemplateEditorState?.template?.id,
        selectedTemplate: selectedTemplate.value,
      };
    });

    let anchorConfig: DropdownOption[] = [];
    const availableAnchors: string[] = [];

    if (props.linkTypeOptions.includes(LinkTypeEnum.ANCHOR)) {
      // We get the current anchors in the template
      anchorConfig = getAnchors();

      if (anchorConfig.length) {
        anchorConfig.forEach((anchor) => {
          availableAnchors.push(anchor.value.toString());
        });
      }
    }

    const getLinkType = () => {
      if (hrefValue.value) {
        if (hrefValue.value.match(/^mailto:/g)) return LinkTypeEnum.EMAIL_ADDRESS;
        if (hrefValue.value.match(/^tel:/g)) return LinkTypeEnum.CALL_NUMBER;
        if (hrefValue.value.match(/^reply:/g)) return LinkTypeEnum.QUICK_REPLY;
        if (hrefValue.value.match(/^#spin_redirect_/g) || hrefValue.value.match(/^#redirect_/g)) {
          if (!isDisplayTemplate(TemplateEditorState.template?.type)) {
            return LinkTypeEnum.TO_NEXT_STEP;
          }
          const template = getTemplateIframeDocument();
          if (template && selectedStructure.value) {
            const selectedElement = template.querySelector(`#${selectedStructure.value.identifier} [data-send-form]`);
            if (selectedElement) {
              return LinkTypeEnum.SEND_FORM;
            }
          }
          return LinkTypeEnum.TO_NEXT_STEP;
        }
        if (hrefValue.value.match(/^#spmClosePopin/g)) return LinkTypeEnum.CLOSE_POPIN;
        if (hrefValue.value.match(/^#send-form_/g)) return LinkTypeEnum.SEND_FORM;
        if (hrefValue.value.match(/^#/g)) return LinkTypeEnum.ANCHOR;
        return LinkTypeEnum.WEB_ADDRESS;
      }
      return LinkTypeEnum.NONE;
    };

    const editTemplateRedirection = async (id: number, name: string, typeTemplate: string) => {
      await updateTemplateConfiguration();
      await activateTemplate(id, name, typeTemplate);
    };

    const linkType: Ref<LinkTypeEnum> = ref(props.initialLinkType ? props.initialLinkType as LinkTypeEnum : getLinkType());

    const isBase64String = ref(false);

    // Calculate phone code length for character counter offset
    const phoneCodeLength = computed(() => {
      const phoneCode = countriesPhonesCodes[phoneCountryAndState.value.country] || '';
      return phoneCode.length;
    });

    // Calculate max length for phone number input
    const computedMaxPhoneLength = computed(() => {
      // If a specific maxPhoneNumberLength is provided (e.g. from WhatsApp), use it
      if (props.maxPhoneNumberLength) {
        return props.maxPhoneNumberLength - phoneCodeLength.value;
      }
      // Otherwise, no limit
      return undefined;
    });

    const handleChange = () => {
      if (linkType.value === LinkTypeEnum.WEB_ADDRESS && isBase64String.value) {
        context.emit('update:modelValue', window.btoa(hrefValue.value));
      } else {
        context.emit('update:modelValue', hrefValue.value);
      }
      // Skip side-effects when only toggling spin redirect prefix
      if (isSpinPrefixToggle) {
        isSpinPrefixToggle = false;
        return;
      }
      if (isWhatsappTemplate(TemplateEditorState.template?.type) || isRichTextingTemplate(TemplateEditorState.template?.type) || (isDisplayTemplate(TemplateEditorState.template?.type) && updateDataSendForm.value)) {
        updateDataSendForm.value = false;
        context.emit('update:linkType', linkType.value);
      } else if (isDisplayTemplate(TemplateEditorState.template?.type) && !updateDataSendForm.value) {
        updateDataSendForm.value = false;
        context.emit('update:removeDataSendForm', true);
      }
    };

    const handleChangeTelToLink = () => {
      // Get the phone code from the selected country
      const phoneCode = countriesPhonesCodes[phoneCountryAndState.value.country] || '';
      // Build the full phone number with country code
      const fullPhoneNumber = `${phoneCode}${callNumberValue.value}`;

      // Try to parse and normalize the phone number
      try {
        const parsed = parsePhoneNumber(fullPhoneNumber);
        if (parsed && parsed.isValid()) {
          // Use the normalized international format
          hrefValue.value = `tel:${parsed.number}`;
          // Update the display value to match the normalized number (without country code)
          const normalizedWithoutCode = parsed.number.replace(`+${parsed.countryCallingCode}`, '');
          if (normalizedWithoutCode !== callNumberValue.value) {
            callNumberValue.value = normalizedWithoutCode;
          }
        } else {
          // If invalid but parseable, still save it for validation to catch later
          hrefValue.value = `tel:${fullPhoneNumber}`;
        }
      } catch (e) {
        // If parsing fails completely, save as-is for validation to catch later
        hrefValue.value = `tel:${fullPhoneNumber}`;
      }

      handleChange();
    };

    const selectTemplate = async (idTemplate: number, label: string, type: string) => {
      const { item } = await Get<Templates>({
        name: 'Templates',
        id: idTemplate,
        keyName: 'id_template',
        fields: ['id_template', 'label', 'type'],
      });

      if (item) {
        selectedTemplate.value = item;
        hrefValue.value = `#redirect_${btoa(idTemplate as unknown as string)}`;
        handleChange();

        if (originCall.value === 'templateBuilder') {
          setRefreshTemplatesList(true);
        }
      }

      displayTemplateModal.value = false;
      store.commit('general/hideTheSpinner');
    };

    const handleChangeMailToLink = () => {
      hrefValue.value = `mailto:${addressValue.value}?subject=${subjectValue.value}&body=${bodyValue.value}`;
      handleChange();
    };

    const refreshImagePreview = async () => {
      if (linkType.value === LinkTypeEnum.DISPLAY_TEMPLATE) {
        let encodedDisplayedTemplate = '';
        if (hrefValue.value) {
          const matchResult = hrefValue.value.match(/#redirect_(.+)/);
          encodedDisplayedTemplate = matchResult && matchResult[1] ? matchResult[1] : '';
        }
        const decodedIdTemplate = Number(atob(encodedDisplayedTemplate));
        if (decodedIdTemplate > 0) {
          imageUrl.value = '';
          const cryptedImageKey = CryptoJS.SHA1(`salt-${decodedIdTemplate}`).toString();

          const displaySuffix = isDisplayTemplate(selectedTemplate.value?.type ?? undefined) ? '-isdisplay' : '';
          const potentialImageUrl = `${baseUrl}${idShop.value}_${cryptedImageKey}${displaySuffix}.png&w=400&${new Date().getTime()}`;
          try {
            await loadImageUntilSuccess(potentialImageUrl);
            imageUrl.value = potentialImageUrl;
          } catch (error) {
            console.error("Erreur lors du chargement de l'image", error);
          }
        }
      }
    };

    // Raw steps: FunnelStepSelector filters game prize steps itself and needs them to offer "Game result"
    const funnelSteps = computed<FunnelStep[]>(() => (
      TemplateEditorState.template ? TemplateEditorState.template.funnel.nextSteps : []
    ) as FunnelStep[]);
    const selectedFunnelStep: Ref<FunnelStep | null> = ref(null);

    const displayNextStep = ref(false);
    const selectedNextStep = ref();
    const nextStepIconMap: Record<string, string> = {
      [NextStepEnum.COLLECT_PERSONAL_INFO]: 'fa-light fa-address-card',
      [NextStepEnum.COLLECT_PHONE_NUMBER]: 'fa-light fa-phone',
      [NextStepEnum.OFFER_VOUCHER]: 'fa-light fa-gift',
      [NextStepEnum.EMPTY_STEP]: 'fa-light fa-file',
      [NextStepEnum.CHOOSE_EXISTING_NEXT_STEP]: 'fa-light fa-link',
      [NextStepEnum.DISPLAY_POPUP]: 'fa-light fa-palette',
    };

    const nextStepOptions = computed(() => {
      let stepOptions: NextStepEnum[] = [];
      if (isDisplayTemplate(TemplateEditorState.template?.type)) {
        stepOptions = [
          NextStepEnum.COLLECT_PERSONAL_INFO,
          NextStepEnum.COLLECT_PHONE_NUMBER,
          NextStepEnum.OFFER_VOUCHER,
          NextStepEnum.EMPTY_STEP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
          NextStepEnum.DISPLAY_POPUP,
        ];
      } else if (isWhatsappTemplate(TemplateEditorState.template?.type) || isRichTextingTemplate(TemplateEditorState.template?.type)) {
        stepOptions = [
          NextStepEnum.EMPTY_STEP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
        ];
      } else {
        stepOptions = [
          NextStepEnum.DISPLAY_POPUP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
        ];
      }

      return stepOptions.map((option) => {
        const isDisplayPopupRenamedAsNew = isDisplayTemplate(TemplateEditorState.template?.type) && option === NextStepEnum.DISPLAY_POPUP;
        const labelKey = isDisplayPopupRenamedAsNew ? 'chooseNewTemplate' : option;
        return {
          label: t(`templateBuilder.modals.nextStepModal.steps.${labelKey}.title`),
          subtitle: t(`templateBuilder.modals.nextStepModal.steps.${labelKey}.subtitle`),
          icon: nextStepIconMap[option] || 'fa-light fa-circle',
          value: option,
          disabled: option === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP && !funnelSteps.value.length && !hasGameWidgetInOtherFunnelTemplates(),
        };
      });
    });

    const handleCancelNextStep = () => {
      displayNextStep.value = false;
      linkType.value = getLinkType();
      selectedNextStep.value = null;
      if (linkType.value === LinkTypeEnum.SEND_FORM && !selectedTemplate.value && !isGamePrizesRedirect.value && !isGameResultRedirect.value) {
        actionAfterSendFormValue.value = SendFormActionTypeEnum.CLOSE;
      }
    };

    const loadingNextStep = ref(false);

    const removePreviousTargetFromFunnel = (previousTemplateId: number) => {
      const identifier = selectedStructure.value?.identifier;
      const editedWidget = identifier
        ? getTemplateIframeDocument()?.querySelector(`#${identifier}`) ?? null
        : null;
      removeStepFromCurrentFunnelIfUnreferenced(previousTemplateId, editedWidget);
    };

    const updateHrefValue = (id: string, prefix = '') => {
      const redirectType = spinBeforeRedirect.value ? '#spin_redirect_' : '#redirect_';
      if (prefix) {
        hrefValue.value = `${prefix}${redirectType}${btoa(id)}`;
      } else {
        hrefValue.value = `${redirectType}${btoa(id)}`;
      }
    };

    const closeDisplayNextStepModal = () => {
      loadingNextStep.value = false;
      displayTemplateModal.value = false;
      displayNextStep.value = false;
      selectedNextStep.value = null;
    };

    const delay = (ms: number) => new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

    const applyCurrentEmbedSettingsToTemplate = (template: any) => {
      if (!template || !state.template) return;
      if (state.template.type !== TemplateTypeEnum.EMBED) return;

      const embedSelector = state.template.configuration?.embedSelector;
      const embedPosition = state.template.configuration?.embedPosition;
      if (embedSelector === undefined && embedPosition === undefined) {
        return;
      }
      applyEmbedSettingsToTemplate(template, embedSelector, embedPosition);
    };

    const showNextStepTemplate = async (templateId: number, templateName: string, templateType: string, templateFromFunnelIndex: number) => {
      if (!TemplateEditorState.leftToolbar.show.widgetsPanel) {
        await hideLeftToolbar();
      }
      await nextTick();
      await activate(templateId, templateName, templateType, templateFromFunnelIndex, false);
    };

    const handleNextStep = async () => {
      // Check if WhatsApp template hash changed and needs confirmation before saving
      if (isWhatsappTemplate(state.template?.type) || isRichTextingTemplate(state.template?.type)) {
        // Sync DOM content into whatsapp/rcs data before checking hash
        await updateTemplateConfiguration();
        const canProceed = checkWhatsappHashChangeAndConfirm(async () => {
          await executeNextStep();
        });
        if (!canProceed) return;
      }
      await executeNextStep();
    };

    const executeNextStep = async () => {
      if (selectedNextStep.value === NextStepEnum.DISPLAY_POPUP) {
        displayTemplateModal.value = true;
        } else if (selectedNextStep.value === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP) {
        if (!selectedFunnelStep.value) {
          showToastError(t('templateBuilder.funnelStepSelector.error'));
          return;
        }

        // Game result: unified handler for game redirect
        if (selectedFunnelStep.value.template?.isGameResult) {
          if (selectedTemplate.value) {
            removePreviousTargetFromFunnel(Number(selectedTemplate.value.id_template));
          }
          hrefValue.value = '#redirect_game_result';
          isGameResultRedirect.value = true;
          isGamePrizesRedirect.value = false;
          selectedTemplate.value = undefined;
          handleChange();
          closeDisplayNextStepModal();
          // Pass the current template ID as the forced attach point since
          // the translation hasn't propagated to state yet
          resyncGameFunnelSteps(state.template?.id);
          return;
        }

        // If switching from a game redirect to a normal step, trigger resync after DOM update
        const wasGameRedirect = isGamePrizesRedirect.value || isGameResultRedirect.value;
        if (wasGameRedirect) {
          isGamePrizesRedirect.value = false;
          isGameResultRedirect.value = false;
        }

        loadingNextStep.value = true;
        const { item } = await Get<Templates>({
          name: 'Templates',
          id: selectedFunnelStep.value.template?.id as unknown as number,
          keyName: 'id_template',
          fields: ['id_template', 'label', 'type'],
        });
        if (item) {
          // Remove old funnel step before adding the new one (redirect change)
          if (selectedTemplate.value && selectedTemplate.value.id_template !== selectedFunnelStep.value.template?.id) {
            removePreviousTargetFromFunnel(Number(selectedTemplate.value.id_template));
          }
          selectedTemplate.value = item;
          if (isWhatsappTemplate(state.template?.type) || isRichTextingTemplate(state.template?.type)) {
            updateHrefValue(selectedFunnelStep.value.template?.id as unknown as string, 'reply:quick_');
          } else {
            updateHrefValue(selectedFunnelStep.value.template?.id as unknown as string);
          }
          handleChange();
          if (wasGameRedirect) {
            nextTick(() => resyncGameFunnelSteps());
          }

          addStepToFunnel(selectedFunnelStep.value);
          if (TemplateEditorState.template && TemplateEditorState.template.originTemplateId) {
            let templateFromFunnelIndex = TemplateEditorState.templatesFromFunnel[TemplateEditorState.template.originTemplateId]
              .findIndex((el) => selectedFunnelStep.value && String(el.id) === String(selectedFunnelStep.value.template?.id));
            if (templateFromFunnelIndex !== -1) {
              await delay(500); // Delay it because of the timeout used in handlePropertyValuesChange
              closeDisplayNextStepModal();
              if (await beforeSaveTemplate(t)) {
                await createHistory(HistoryType.INTERMEDIATE_POINT_OF_SAVE, true);
                updateSectionsInState(false);
                store.commit('general/showTheSpinner');
                try {
                  if (await saveTemplate()) {
                    setRefreshTemplatesList(true);
                    if (!selectedSyncElement.value) {
                      updateSectionsInState(false);

                      // Recalculate templateFromFunnelIndex if an update of the step has been done
                      templateFromFunnelIndex = TemplateEditorState.templatesFromFunnel[TemplateEditorState.template.originTemplateId]
                        .findIndex((el) => selectedFunnelStep.value && String(el.id) === String(selectedFunnelStep.value.template?.id));
                      if (templateFromFunnelIndex !== -1) {
                        await showNextStepTemplate(item.id_template, item.label, item.type as string, templateFromFunnelIndex);
                      }
                    }
                  }
                } finally {
                  store.commit('general/hideTheSpinner');
                }
              }
            }
          }
        } else {
          showToastError(t('errorMessages.GENERIC_ERROR'));
        }
        loadingNextStep.value = false;
      } else {
        loadingNextStep.value = true;
        store.commit('general/showTheSpinner');
        const result = await createNextStepTemplate(
          idShop.value,
          selectedNextStep.value,
          generateNextStepName(
            TemplateEditorState.template?.informations.name || '',
            getTemplateParentTypeByType(TemplateEditorState.template?.type),
            t,
            selectedTemplate.value,
          ),
          t,
          props.idCampaign,
          props.originCall,
        );
        if (!result) {
          showToastError(t('errorMessages.GENERIC_ERROR'));
          store.commit('general/hideTheSpinner');
        } else {
          const { item, template } = result;
          applyCurrentEmbedSettingsToTemplate(template);

          // Remove old funnel step before adding the new one (redirect change)
          if (selectedTemplate.value && Number(selectedTemplate.value.id_template) !== template.id) {
            removePreviousTargetFromFunnel(Number(selectedTemplate.value.id_template));
          }

          selectedTemplate.value = item;

          // Build save input for the new template (createNextStepTemplate no longer persists it)
          const newTemplateUpdate = {
            id: template.id,
            content: template.content,
            type: template.type,
            idCampaign: template.idCampaign,
            informations: template.informations,
            sections: template.sections,
            configuration: { ...template.configuration },
          };

          if (template) {
            addTemplateToFunnels(template);
            // Discover and attach any sub-redirections the new template may contain
            await discoverAndAttachSubFunnels(template);
          }

          if (isWhatsappTemplate(state.template?.type) || isRichTextingTemplate(state.template?.type)) {
            updateHrefValue(template.id as unknown as string, 'reply:quick_');
          } else {
            updateHrefValue(template.id as unknown as string);
          }
          handleChange();

          if (originCall.value === 'templateBuilder') {
            setRefreshTemplatesList(true);
          }

          if (TemplateEditorState.template && TemplateEditorState.template.originTemplateId) {
            const templateFromFunnelIndex = TemplateEditorState.templatesFromFunnel[TemplateEditorState.template.originTemplateId]
              .findIndex((el) => selectedTemplate.value && String(el.id) === String(selectedTemplate.value.id_template));
            if (templateFromFunnelIndex !== -1) {
              await delay(500); // Delay it because of the timeout used in handlePropertyValuesChange
              closeDisplayNextStepModal();
              // Save the current template (with the new redirect button) + the new template in a single batch
              updateSectionsInState(false);
              await updateTemplateConfiguration();
              setRefreshTemplatesList(true);
              if (!selectedSyncElement.value) {
                await saveTemplate([newTemplateUpdate]);
                await showNextStepTemplate(item.id_template, item.label, item.type as string, templateFromFunnelIndex);
              }
            } else {
              closeDisplayNextStepModal();
              await saveTemplate([newTemplateUpdate]);
            }
          } else {
            closeDisplayNextStepModal();
            await saveTemplate([newTemplateUpdate]);
          }
          store.commit('general/hideTheSpinner');
        }
      }
    };

    const handleChooseTemplate = async (idTemplate: number) => {
      const { item } = await Get<Templates>({
        name: 'Templates',
        id: idTemplate,
        keyName: 'id_template',
        fields: ['id_template', 'label', 'type'],
      });

      if (item) {
        // Remove old funnel step before adding the new one (redirect change)
        if (selectedTemplate.value && Number(selectedTemplate.value.id_template) !== idTemplate) {
          removePreviousTargetFromFunnel(Number(selectedTemplate.value.id_template));
        }
        selectedTemplate.value = item;

        const preparedTemplate = await prepareTemplate(idTemplate, item.label, item.type as string);
        if (preparedTemplate) {
          addTemplateToFunnels(preparedTemplate.template);
          // Discover and attach any sub-redirections the added template may contain
          await discoverAndAttachSubFunnels(preparedTemplate.template);
          // Sync game funnel steps if the chosen template has a game widget
          syncGameFunnelStepsForTemplate(preparedTemplate.template.id);
        }

        updateHrefValue(idTemplate as unknown as string);
        handleChange();

        if (originCall.value === 'templateBuilder') {
          setRefreshTemplatesList(true);
        }

        if (TemplateEditorState.template && TemplateEditorState.template.originTemplateId) {
          await delay(500);
          const templateFromFunnelIndex = TemplateEditorState.templatesFromFunnel[TemplateEditorState.template.originTemplateId]
            .findIndex((el) => selectedTemplate.value && String(el.id) === String(selectedTemplate.value.id_template));
          if (templateFromFunnelIndex !== -1) {
            closeDisplayNextStepModal();
            await delay(500); // Delay it because of the timeout used in handlePropertyValuesChange
            updateSectionsInState(false);
            await updateTemplateConfiguration();
            if (!selectedSyncElement.value) {
              await saveTemplate();
              await showNextStepTemplate(item.id_template, item.label, item.type as string, templateFromFunnelIndex);
            }
            store.commit('general/hideTheSpinner');
          }
        }
      } else {
        showToastError(t('errorMessages.GENERIC_ERROR'));
        store.commit('general/hideTheSpinner');
      }
    };

    // Check if form contains an email or phone widget
    const formHasEmailOrPhone = computed(() => {
      const identifier = selectedStructure.value?.identifier;
      if (!identifier || !state.template?.configuration?.forms) return false;
      const allForms = state.template.configuration.forms;
      for (const [key, config] of Object.entries(allForms) as [string, any][]) {
        if (key === identifier) continue; // skip the button itself
        if (config.type === 'widget-input-email' || config.type === 'widget-input-phone') {
          return true;
        }
      }
      return false;
    });

    const allowDuplicateEmail = computed(() => {
      const identifier = selectedStructure.value?.identifier;
      if (!identifier) return false;
      return !!getFormFieldConfig(identifier, 'allowDuplicateEmail');
    });

    const allowResubmitDefinitions = computed((): number[] => {
      const identifier = selectedStructure.value?.identifier;
      if (!identifier) return [];
      return (getFormFieldConfig(identifier, 'allowResubmitDefinitions') as number[]) || [];
    });

    // Per-action submission policy shown inside each FormActionCard — both use the
    // positive "Accepter la resoumission" framing (symmetric with the consent option):
    // - consent     → maps to allowDuplicateEmail (boolean)
    // - custom data → maps to allowResubmitDefinitions (per-definition allow-list;
    //   default OFF = blocked when the contact already submitted, checked = accepted)
    const submissionOptionFor = (actionStatus: FormActionStatus) => {
      const actionId = actionStatus.action.id;
      if (actionId === 'consent') {
        return { kind: 'allowResubmit' as const, value: allowDuplicateEmail.value };
      }
      if (actionId.startsWith('custom_data_storage_')) {
        const schemaId = parseInt(actionId.replace('custom_data_storage_', ''), 10);
        return {
          kind: 'allowResubmitData' as const,
          value: !isNaN(schemaId) && allowResubmitDefinitions.value.includes(schemaId),
        };
      }
      return null;
    };

    const handleToggleSubmissionOption = (actionId: string, value: boolean) => {
      const identifier = selectedStructure.value?.identifier;
      if (!identifier) return;
      if (actionId === 'consent') {
        updateFormFieldConfig(identifier, 'allowDuplicateEmail', value);
        return;
      }
      if (actionId.startsWith('custom_data_storage_')) {
        const schemaId = parseInt(actionId.replace('custom_data_storage_', ''), 10);
        if (isNaN(schemaId)) return;
        // value = checked = "accept re-submission" → add to the allow-list.
        const current = (
          (getFormFieldConfig(identifier, 'allowResubmitDefinitions') as number[]) || []
        ).slice();
        const idx = current.indexOf(schemaId);
        if (value && idx === -1) current.push(schemaId);
        else if (!value && idx !== -1) current.splice(idx, 1);
        updateFormFieldConfig(identifier, 'allowResubmitDefinitions', current);
      }
    };

    const handleToggleAction = async (actionId: string, isEnabled: boolean) => {
      const identifier = selectedStructure.value?.identifier;
      if (!identifier) {
        return;
      }

      // Check if the widget exists in state, if not create it
      if (!getFormByIdentifier(identifier)) {
        addNewForm(identifier, 'widget-button');
      }

      // Track user explicit disabling/enabling
      const currentDisabled: string[] = getFormFieldConfig(identifier, 'user_disabled_actions') || [];
      let updatedDisabled: string[];

      if (isEnabled) {
        // User explicitly enables → remove from disabled list
        updatedDisabled = currentDisabled.filter((a) => a !== actionId);
      } else {
        // User explicitly disables → add to disabled list
        if (!currentDisabled.includes(actionId)) {
          updatedDisabled = [...currentDisabled, actionId];
        } else {
          updatedDisabled = currentDisabled;
        }
      }

      updateFormFieldConfig(identifier, 'user_disabled_actions', updatedDisabled);

      // Re-evaluate — evaluateFormActions will now use user_disabled_actions
      // and sync actions_to_take automatically
      formActionStatuses.value = await evaluateFormActions(identifier);
    };

    // Consentement au suivi des ouvertures (implicite, niveau bouton) : le toggle
    // stocke uniquement l'opt-out du marchand (défaut ON = flag absent).
    const handleToggleTrackingConsent = async (value: boolean) => {
      const identifier = selectedStructure.value?.identifier;
      if (!identifier) return;
      if (!getFormByIdentifier(identifier)) {
        addNewForm(identifier, 'widget-button');
      }
      // Flag POSITIF (opt-in) : on stocke l'état résolu du suivi. Sa présence marque le form
      // comme configuré ; les anciens forms (sans ce flag) ne trackent pas côté back.
      updateFormFieldConfig(identifier, 'tracking_consent_enabled', value);
      formActionStatuses.value = await evaluateFormActions(identifier);
    };

    // CTA « Ajouter le widget Divulgation » : insertion assistée (canal email) puis ré-éval.
    const handleAddDisclosureWidget = async () => {
      const identifier = selectedStructure.value?.identifier;
      const ok = await insertDisclosureWidgetForEmail(identifier || undefined);
      if (!ok) {
        showToastError(t('errorMessages.GENERIC_ERROR'));
        return;
      }
      if (identifier) {
        formActionStatuses.value = await evaluateFormActions(identifier);
      }
    };

    const handleActionAfterSendChange = (close = false, prefix = '#send-form_') => {
      if (!close) {
        const actionValue = `toUrl:${actionAfterSendFormHrefValue.value}`;
        hrefValue.value = `${prefix}${btoa(actionValue)}`;
      } else {
        hrefValue.value = `${prefix}close`;
      }
      selectedTemplate.value = undefined;
      handleChange();
    };

    watch(displayNextStep, () => {
      if (!displayNextStep.value) {
        handleCancelNextStep();
      }
    });

    let hrefWatcherInitialized = false;
    const stopWatchHrefValue = watch(hrefValue, async (values, oldValues) => {
      await refreshImagePreview();
      if (values !== oldValues) {
        if (hrefValue.value) {
          linkType.value = getLinkType();
        }
        if (hrefWatcherInitialized) {
          handleChange();
        }
      }
      hrefWatcherInitialized = true;
    }, { immediate: true });

    const retrieveTemplateFromHref = async (href: string) => {
      try {
        // Detect game prizes redirect
        if (href === '#redirect_game_prizes') {
          isGamePrizesRedirect.value = true;
          isGameResultRedirect.value = false;
          spinBeforeRedirect.value = false;
          selectedTemplate.value = undefined;
          return;
        }
        // Detect game result redirect
        if (href === '#redirect_game_result') {
          isGameResultRedirect.value = true;
          isGamePrizesRedirect.value = false;
          spinBeforeRedirect.value = false;
          selectedTemplate.value = undefined;
          return;
        }
        isGamePrizesRedirect.value = false;
        isGameResultRedirect.value = false;
        // Detect spin_redirect_ prefix
        const spinMatch = href.match(/#spin_redirect_(.+)/);
        if (spinMatch) {
          spinBeforeRedirect.value = true;
          const id = atob(spinMatch[1]);
          if (!id || isNaN(Number(id)) || Number(id) <= 0) {
            selectedTemplate.value = undefined;
            return;
          }
          const { item } = await Get<Templates>({
            name: 'Templates',
            id,
            keyName: 'id_template',
            fields: ['id_template', 'label', 'type'],
          });
          selectedTemplate.value = item || undefined;
          return;
        }
        spinBeforeRedirect.value = false;
        const redirectMatch = href.match(/#redirect_(.+)/);
        const id = redirectMatch ? atob(redirectMatch[1]) : '';
        if (!id || isNaN(Number(id)) || Number(id) <= 0) {
          selectedTemplate.value = undefined;
          return;
        }
        // Retrieve template informations
        const { item } = await Get<Templates>({
          name: 'Templates',
          id,
          keyName: 'id_template',
          fields: ['id_template', 'label', 'type'],
        });
        if (item) {
          selectedTemplate.value = item;
        } else {
          selectedTemplate.value = undefined;
        }
      } catch (e) { /* unable to decode template */ }
    };

    // Watch for changes in selected structure AND forms config to reload form actions
    watch(
      () => {
        const identifier = selectedStructure.value?.identifier;
        // Also watch the forms config to trigger reload when template is loaded from DB
        const formsConfig = identifier ? state.template?.configuration?.forms?.[identifier] : null;
        return { identifier, formsConfig, linkType: linkType.value };
      },
      async ({ identifier, formsConfig, linkType: currentLinkType }) => {
        if (identifier && currentLinkType === LinkTypeEnum.SEND_FORM) {
          formActionStatuses.value = await evaluateFormActions(identifier);
        }
      },
      { deep: true },
    );

    watch(() => linkType.value, async (values, oldValues) => {
      if (values !== oldValues) {
        updateDataSendForm.value = false;
        const wasGamePrizesRedirect = isGamePrizesRedirect.value;
        isGamePrizesRedirect.value = false;
        // Remove widget-button config if changing away from SEND_FORM
        const identifier = selectedStructure.value?.identifier;
        if (identifier && oldValues === LinkTypeEnum.SEND_FORM && linkType.value !== LinkTypeEnum.SEND_FORM) {
          removeFormConfig(identifier);
        }

        if (linkType.value === LinkTypeEnum.CLOSE_POPIN) {
          hrefValue.value = '#spmClosePopin';
        } else if (linkType.value === LinkTypeEnum.EMAIL_ADDRESS) {
          const matches: RegExpExecArray|null = mailToPattern.exec(hrefValue.value ?? '');
          if (matches) {
            // eslint-disable-next-line
            addressValue.value = matches[1];
            // eslint-disable-next-line
            subjectValue.value = matches[2];
            // eslint-disable-next-line
            bodyValue.value = matches[3];
          }
        } else if (linkType.value === LinkTypeEnum.DISPLAY_TEMPLATE) {
          try {
            const id = atob(hrefValue.value.replace('#redirect_', '') ?? ''); // Extract and decode template id
            // Retrieve template informations
            const { item, err } = await Get<Templates>({
              name: 'Templates',
              id,
              keyName: 'id_template',
              fields: ['id_template', 'label', 'type'],
            });
            if (item) {
              selectedTemplate.value = item;
            }
          } catch (e) { /* unable to decode template */ }
        } else if (linkType.value === LinkTypeEnum.ANCHOR) {
          if (availableAnchors.length && !availableAnchors.includes(hrefValue.value)) {
            // If the link was not an anchor, we set the first anchor as the href value
            [hrefValue.value] = availableAnchors;
          }
        } else if (oldValues && linkType.value === LinkTypeEnum.NONE) {
          hrefValue.value = '';
          selectedTemplate.value = undefined;
        } else if (linkType.value === LinkTypeEnum.CALL_NUMBER) {
          const phoneNumber = hrefValue.value.replace('tel:', '');
          try {
            // Try to parse the phone number to extract country and national number
            const parsed = parsePhoneNumber(phoneNumber);
            if (parsed && parsed.country) {
              phoneCountryAndState.value.country = parsed.country;
              // Extract the number without country code by removing the country calling code
              const countryCallingCode = `+${parsed.countryCallingCode}`;
              callNumberValue.value = phoneNumber.replace(countryCallingCode, '');
            } else {
              // Fallback if parsing fails - try to extract country code manually
              const countryCodeMatch = phoneNumber.match(/^\+(\d{1,4})/);
              if (countryCodeMatch) {
                callNumberValue.value = phoneNumber.replace(countryCodeMatch[0], '');
              } else {
                callNumberValue.value = phoneNumber;
              }
            }
          } catch (e) {
            // Fallback if parsing fails - try to extract country code manually
            const countryCodeMatch = phoneNumber.match(/^\+(\d{1,4})/);
            if (countryCodeMatch) {
              callNumberValue.value = phoneNumber.replace(countryCodeMatch[0], '');
            } else {
              callNumberValue.value = phoneNumber;
            }
          }
        } else if (linkType.value === LinkTypeEnum.QUICK_REPLY) {
          try {
            if (hrefValue.value.match(/#redirect_/g)) {
              // Load template first to avoid triggering modal in actionAfterSendFormValue watch
              await retrieveTemplateFromHref(hrefValue.value);
              actionAfterSendFormValue.value = SendFormActionTypeEnum.TO_NEXT_STEP;
              actionAfterSendFormHrefValue.value = '';
            } else {
              const actionAfter = hrefValue.value.replace('reply:quick_', '');
              if (actionAfter === 'close' || actionAfter.trim() === '') {
                hrefValue.value = 'reply:quick_close';
                actionAfterSendFormValue.value = SendFormActionTypeEnum.CLOSE;
              }
            }
          } catch (e) {
            hrefValue.value = 'reply:quick_close';
            actionAfterSendFormValue.value = SendFormActionTypeEnum.CLOSE;
          }
        } else if (linkType.value === LinkTypeEnum.TO_NEXT_STEP) {
          await retrieveTemplateFromHref(hrefValue.value);
          if (!selectedTemplate.value && !isGamePrizesRedirect.value && !isGameResultRedirect.value) {
            displayNextStep.value = true;
          }
        } else if (linkType.value === LinkTypeEnum.SEND_FORM) {
          // Create widget-button config if it doesn't exist
          const buttonIdentifier = selectedStructure.value?.identifier;
          updateDataSendForm.value = true;

          // Only initialize widget config if this is not the initial load (oldValues exists)
          // This prevents overwriting configs loaded from DB during template initialization
          if (buttonIdentifier && !state.template?.configuration?.forms?.[buttonIdentifier] && oldValues !== undefined) {
            // Get default enabled actions based on available form fields
            const defaultActions = await getDefaultEnabledActions();
            const initialConfig = defaultActions.length > 0 ? { actions_to_take: defaultActions } : {};

            addNewForm(buttonIdentifier, 'widget-button', initialConfig);
          }

          // Always load form actions when opening SEND_FORM panel
          if (buttonIdentifier) {
            // Use nextTick to ensure state is fully updated
            await nextTick();
            formActionStatuses.value = await evaluateFormActions(buttonIdentifier);
          }

          try {
            if (hrefValue.value.match(/^#(spin_)?redirect_/g)) {
              // Load template first to avoid triggering modal in actionAfterSendFormValue watch
              await retrieveTemplateFromHref(hrefValue.value);
              actionAfterSendFormValue.value = SendFormActionTypeEnum.TO_NEXT_STEP;
              actionAfterSendFormHrefValue.value = '';
            } else {
              const actionAfter = hrefValue.value.replace('#send-form_', '');
              if (actionAfter === 'close' || actionAfter.trim() === '') {
                hrefValue.value = '#send-form_close';
                actionAfterSendFormValue.value = SendFormActionTypeEnum.CLOSE;
              } else {
                const decodedAction = atob(actionAfter);
                if (decodedAction.startsWith('toUrl:')) {
                  actionAfterSendFormValue.value = SendFormActionTypeEnum.TO_URL;
                  actionAfterSendFormHrefValue.value = decodedAction.replace('toUrl:', '');
                }
              }
            }
          } catch (e) {
            hrefValue.value = '#send-form_close';
            actionAfterSendFormValue.value = SendFormActionTypeEnum.CLOSE;
          }
        }

        if (oldValues && hrefValue.value) {
          // We emit only if it's not the initialization of the field and hrefValue is set
          handleChange();
        }

        // If we were redirecting to game prizes and link type changed, re-sync game steps
        // so they move back to the wheel template's step in the funnel tree
        if (wasGamePrizesRedirect) {
          nextTick(() => resyncGameFunnelSteps());
        }
      }
    }, { immediate: true });

    // Watch for changes in country selection for phone numbers
    watch(() => phoneCountryAndState.value.country, () => {
      if (linkType.value === LinkTypeEnum.CALL_NUMBER && callNumberValue.value) {
        handleChangeTelToLink();
      }
    });

    watch(actionAfterSendFormValue, () => {
      if (actionAfterSendFormValue.value === SendFormActionTypeEnum.CLOSE) {
        actionAfterSendFormHrefValue.value = '';
        selectedTemplate.value = undefined;
        let prefix = '#send-form_';
        if (isWhatsappTemplate(state.template?.type) || isRichTextingTemplate(state.template?.type)) {
          prefix = 'reply:quick_';
        }
        handleActionAfterSendChange(true, prefix);
      } else if (actionAfterSendFormValue.value === SendFormActionTypeEnum.TO_NEXT_STEP && !selectedTemplate.value && !isGamePrizesRedirect.value && !isGameResultRedirect.value) {
        updateDataSendForm.value = true;
        displayNextStep.value = true;
      }
    });

    watch([addressValue, subjectValue, bodyValue], () => {
      handleChangeMailToLink();
    });

    onMounted(async () => {
      // Check if value need to be translated into base64 or not
      if (props.translation && (props.translation.fieldType === 'data-redirect' || props.translation.attributeName === 'data-redirect')) {
        const template = getTemplateIframeDocument();
        if (template && props.translation) {
          const element = template.body.querySelector(`[${TRANSLATION_ATTRIBUTE}-data-redirect="LANG_${props.translation.key}"]`);
          if (element) {
            const actionTypes = Object.values(ActionTypeEnum).map((actionType) => `spm_action_${actionType}`);
            // eslint-disable-next-line no-restricted-syntax
            for (const actionType of actionTypes) {
              if (element.classList.contains(actionType)) {
                isBase64String.value = true;
                break;
              }
            }
          }
        }
      }
      if (linkType.value === LinkTypeEnum.WEB_ADDRESS && hrefValue.value.trim() && isBase64String.value) {
        hrefValue.value = window.atob(hrefValue.value);
      }
      await refreshImagePreview();
    });

    const handleHighlight = () => {
      if (props.translation) {
        highlightWidgetOnTranslationClicked(props.translation);
      }
    };

    const baseInputTextRef = ref();
    const dropdownRef = ref();

    // Used to focus field when clicking widget from iframe
    const focus = () => {
      if (baseInputTextRef.value) {
        baseInputTextRef.value.focus();
      }
    };

    // Used to scroll into the field when clicking widget from iframe
    const scrollIntoView = () => {
      if (dropdownRef.value) {
        dropdownRef.value.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const handleActivateGroup = () => {
      if (props.translation) {
        context.emit('activate-group', props.translation.groupKey);
      }
    };

    const handleDeactivateGroup = () => {
      if (props.translation) {
        context.emit('deactivate-group', props.translation.groupKey);
      }
    };

    const showNextStepAfterSendMessage = computed(() => (isWhatsappTemplate(state.template?.type) || isRichTextingTemplate(state.template?.type)) && linkType.value === LinkTypeEnum.QUICK_REPLY);

    /** Full template label when the next-step card shows a funnel template (ellipsis in UI). */
    const nextStepInfoNameTooltip = computed(() => {
      if (isGamePrizesRedirect.value || isGameResultRedirect.value) {
        return undefined;
      }
      const label = selectedTemplate.value?.label;
      if (!label) {
        return undefined;
      }
      return { value: label, escape: true, position: 'top' };
    });

    const selectedTemplateLabelTooltip = computed(() => {
      const label = selectedTemplate.value?.label;
      if (!label) {
        return undefined;
      }
      return { value: label, escape: true, position: 'top' };
    });

    onBeforeUnmount(() => {
      baseInputTextRef.value = null;
      dropdownRef.value = null;
      stopWatchHrefValue();
    });

    return {
      t,
      options,
      anchorConfig,
      hrefValue,
      imageUrl,
      linkType,
      phoneCodeLength,
      computedMaxPhoneLength,
      displayTemplateModal,
      selectedTemplate,
      addressValue,
      subjectValue,
      bodyValue,
      callNumberValue,
      phoneCountryAndState,
      selectTemplate,
      editTemplateRedirection,
      displayTemplatePreview,
      LinkTypeEnum,
      baseLinkData,
      handleHighlight,
      baseInputTextRef,
      focus, // Used to focus field when clicking widget from iframe
      scrollIntoView, // Used to scroll into the field when clicking widget from iframe
      handleActivateGroup,
      handleDeactivateGroup,
      displayNextStep,
      handleCancelNextStep,
      handleToggleAction,
      selectedNextStep,
      nextStepOptions,
      handleNextStep,
      handleChooseTemplate,
      actionAfterSendFormValue,
      SendFormActionTypeEnum,
      sendFormOptions,
      actionAfterSendFormHrefValue,
      handleActionAfterSendChange,
      loadingNextStep,
      funnelSteps,
      selectedFunnelStep,
      NextStepEnum,
      handleChangeTelToLink,
      formActionStatuses,
      sortedAllActions,
      formHasEmailOrPhone,
      submissionOptionFor,
      handleToggleSubmissionOption,
      handleToggleTrackingConsent,
      handleAddDisclosureWidget,

      afterSendMessageOptions,
      showNextStepAfterSendMessage,
      nextStepInfoNameTooltip,
      selectedTemplateLabelTooltip,
      isGamePrizesRedirect,
      isGameResultRedirect,
      spinBeforeRedirect,
      currentTemplateHasGame,
    };
  },
});
